/* eslint-env browser */
/**
 * Created by fers on 07.03.16.
 */

/*eslint no-console: 0*/

import {mock} from 'mocktail';
import Relay from 'react-relay';
import MetaDataHelper from '../../common/fw/MetaDataHelper.ts';
import EventTracker from '../app/EventTracker';
import {getLocalesMyAppSupports} from '../../common/locales';
import mutationError from '../channel-defs/mutationError';

class EditorSaver {
    static CREATE = 'create';
    static DELETE = 'delete';

    // eslint-disable-next-line max-params
    constructor(metadata : MetaDataHelper, mutation, fatQuery, extraConfig, actionType : string = null, copyFunc) {
        this.metadata = metadata;
        this.mutation = mutation;
        this.fatQuery = fatQuery;
        this.extraConfig = extraConfig;
        this.isCreate = actionType === EditorSaver.CREATE;
        this.isDelete = actionType === EditorSaver.DELETE;
        this.copyFunc = copyFunc;
    }

    _trim(str) {
        if (str) {
            return ("" + str).trim();
        }

        return str;
    }

    // eslint-disable-next-line max-params
    copyData(data, storeData, defaultValue, columns) {
        /* we support array or map of keys and their values */
        let colArray = columns;

        if (colArray && !Array.isArray(colArray)) {
            colArray = Object.keys(colArray);
        }

        let fields = this.metadata.fields();
        if (!colArray) {
            colArray = Object.keys(fields);
        }

        /* filter out not editable fields */
        colArray = colArray.filter((fieldKey) => this.metadata.isFieldEditable(fieldKey));

        /* add hiddenCreate fields */
        if (this.isCreate) {
            for (let fieldKey of Object.keys(fields)) {
                if (fields[fieldKey].hiddenCreate) {
                    colArray.push(fieldKey);
                }
            }
        }

        for (let column of colArray) {
            if (fields[column] && fields[column].type === "object" && typeof data[column] !== "string") {
                storeData[column] = JSON.stringify(data[column]);
            } else if (fields[column].translatable) {
                if (!storeData[column + "_l"]) {
                    storeData[column + "_l"] = [];
                }

                for (let locale of getLocalesMyAppSupports()) {
                    let value = data[column + "_" + locale];
                    if (fields[column].trim) {
                        value = this._trim(value);
                    }

                    storeData[column + "_l"].push({
                        locale: locale,
                        content: value
                    });
                }
            } else if (fields[column] && fields[column].trim) {
                storeData[column] = this._trim(data[column]);
            } else if (fields[column].type === "relation") {
                if (data[column + "_id"]) {
                    storeData[column + "_id"] = data[column + "_id"];
                } else {
                    storeData[column + "_id"] = data[column];
                }
            } else if (fields[column].type === "enums") {
                if (data[column]) {
                    if (typeof data[column] === "string") {
                        storeData[column] = data[column].split(",").map((str) => str.trim());
                    } else {
                        storeData[column] = data[column];
                    }
                }
            } else if (fields[column].type === "int" || fields[column].type === "float" || fields[column].type === "decimal") {
                if (data[column] === '') {
                    storeData[column] = null;
                } else if (typeof data[column] === 'string') {
                    if (fields[column].type === "int") {
                        storeData[column] = parseInt(data[column].trim(), 10);
                    } else {
                        storeData[column] = parseFloat(data[column].trim(), 10);
                    }
                } else {
                    storeData[column] = data[column];
                }
            } else {
                storeData[column] = data[column];
            }
        }

        let relations = this.metadata.relations();
        for (let relationKey of Object.keys(relations)) {
            let relation = relations[relationKey];
            if ('inlineStore' in relation && relation.inlineStore) {
                storeData[relationKey] = JSON.stringify(data[relationKey]);
            }
        }

        return storeData;
    }

    // Use this method to prepare the variables that will be used as
    // input to the mutation. Our ‘likeStory’ mutation takes exactly
    // one variable as input – the ID of the story to like.
    getAllVariables(data, defaultValue, columns) {
        let self = this;
        let storeData = {};

        let primary = self.metadata.getPrimaryKey();

        if (self.isDelete) {
            storeData[primary.key] = data[primary.key];
            console.log("delete", storeData);

            return storeData;
        }

        if (self.copyFunc) {
            storeData = self.copyFunc(data, storeData, defaultValue, columns);
        } else {
            storeData = self.copyData(data, storeData, defaultValue, columns);
        }

        let fields = self.metadata.fields();

        /* in create mode, we have to copy hiddenCreate fields from defaultValue */
        if (self.isCreate && defaultValue) {
            for (let field of Object.keys(fields)) {
                if (fields[field].hiddenCreate) {
                    if (fields[field].type === "relation") {
                        storeData[field + "_id"] = defaultValue[field + "_id"];
                    } else {
                        storeData[field] = defaultValue[field];
                    }
                }
            }
        }

        /* copy primary key to be sure it is in the data (it is not editable and we send only changed fields */
        if (self.isCreate !== true) {

            /* we copy primary Key only when it is present */
            if (primary && data[primary.key]) {
                storeData[primary.key] = data[primary.key];
            }
        }

        console.log("Storing ", storeData);

        return storeData;
    }


    _getMyMutation(data, defaultValue, columns) {
        let self = this;

        let type = "update";
        if (this.isDelete) {
            type = "delete";
        } else if (this.isCreate) {
            type = "create";
        }

        EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
            event: 'channel-' + type + '-' + self.metadata.entity(),
            operation: 'update',
            entity: self.metadata.entity(),
            type: 'application'
        });

        let mutation = class extends Relay.Mutation {
            // This method should return a GraphQL operation that represents
            // the mutation to be performed. This presumes that the server
            // implements a mutation type named ‘likeStory’.
            getMutation() {
                return self.mutation;
            }
            // Use this method to prepare the variables that will be used as
            // input to the mutation. Our ‘likeStory’ mutation takes exactly
            // one variable as input – the ID of the story to like.
            getVariables() {
                return self.getAllVariables(data, defaultValue, columns);
            }

            // Use this method to design a ‘fat query’ – one that represents every
            // field in your data model that could change as a result of this mutation.
            // Liking a story could affect the likers count, the sentence that
            // summarizes who has liked a story, and the fact that the viewer likes the
            // story or not. Relay will intersect this query with a ‘tracked query’
            // that represents the data that your application actually uses, and
            // instruct the server to include only those fields in its response.
            getFatQuery() {
                return self.fatQuery;
            }

            // These configurations advise Relay on how to handle the LikeStoryPayload
            // returned by the server. Here, we tell Relay to use the payload to
            // change the fields of a record it already has in the store. The
            // key-value pairs of ‘fieldIDs’ associate field names in the payload
            // with the ID of the record that we want updated.
            getConfigs() {

                let config = [];
                let primary = self.metadata.getPrimaryKey();

                if (self.isCreate) {
                    if (self.extraConfig) {
                        self.extraConfig.forEach((c) => config.push(c));
                    }
                } else if (primary) {
                    let fieldIDs = {};
                    fieldIDs['updatedNode'] = data[primary.key];
                    config.push({
                        type: 'FIELDS_CHANGE',
                        fieldIDs: fieldIDs,
                    });
                }

                if (self.isDelete) {
                    let parent = self.metadata.getParentField(data);

                    if (parent && primary && self.metadata.parentRelation) {
                        config.push({
                            type: 'NODE_DELETE',
                            parentName: parent.key,
                            parentID: data[parent.key],
                            connectionName: self.metadata.parentRelation,
                            deletedIDFieldName: 'updatedNode{' + primary.key + "}",
                        });
                    }
                }

                config.push({
                    type: 'REQUIRED_CHILDREN',
                    children: [self.fatQuery],
                });

                return config;
            }

            // This mutation has a hard dependency on the story's ID. We specify this
            // dependency declaratively here as a GraphQL query fragment. Relay will
            // use this fragment to ensure that the story's ID is available wherever
            // this mutation is used.
            static fragments = {
                /*		organisation: () => Relay.QL`
                 fragment on Organisation {
                 name,
                 description
                 }
                 `, */
            };
        };

        return mutation;
    }


    myOnFailure = (transaction) => {
        mutationError(transaction, false);
    };

    // eslint-disable-next-line max-params
    save(data, defaultValue, columns, onSuccess, onFailure) {
        return new Promise((resolve) => {
            if (!onFailure) {
                onFailure = this.myOnFailure;
            }

            let mutation = this._getMyMutation(data, defaultValue, columns);

            let variables = this.getAllVariables(data, defaultValue, columns);
            // To perform a mutation, pass an instance of one to `Relay.Store.commitUpdate`
            Relay.Store.commitUpdate(new mutation({data: data, variables: variables}), {
                onFailure: (transaction) => {
                    try {
                        onFailure(transaction);
                    } finally {
                        // IMPORTANT:
                        // Relay calls onFailure but our Editor promise must also settle,
                        // otherwise BasicEditor stays stuck in the "saving" state forever.
                        // We resolve (not reject) to avoid bubbling unhandled rejections
                        // from Formsy submit handlers; UI error is handled by mutationError(...).
                        resolve(null);
                    }
                },
                onSuccess: (response) => {
                    resolve(response);

                    if (onSuccess) {
                        setTimeout(() => {
                            onSuccess(response);
                        }, 0);
                    }
                }
            });

        });
    }

}

export default mock(EditorSaver, 'EditorSaver');
